import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { loadCampaign, loadBundleCountries, readAdGroup, readScheduleCriterions, readAdGroupScheduleCriterions, modelToViewModelForAdGroupSetting, saveAdGroup, readTargetCriterion, readAdGroupTargetCriterion, readLocationCriterions, readAdGroupLocationCriterions } from '@bingads-webui-campaign-react/search-campaign-data';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

const defaultAdGroupSettings = {
  Name: 'Ad Group 1',
  AdGroupType: 'SearchStandard',
  SearchBid: 5,
  Status: 'Draft',
};

export const useBidAndTargetRepository = () => {
  const { scope } = useContext(ScopeContext) || {};
  const {
    cid, aid, campaignId,
  } = scope.values;
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const permissions = useContext(PermissionsContext);
  const { state } = useWizardService();
  const { adGroupId } = state.context.wizardData;
  const i18n = useContext(I18nContext);
  const load = useCallback(() => {
    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      adGroup: adGroupId ? readAdGroup(cid, aid, campaignId, adGroupId, i18n) : Promise.resolve(defaultAdGroupSettings),
      campaignScheduleCriterion: readScheduleCriterions(cid, aid, campaignId),
      adGroupScheduleCriterion: adGroupId ? readAdGroupScheduleCriterions(cid, aid, campaignId, adGroupId) : Promise.resolve(null),
      campaignTargetCriterion: readTargetCriterion(cid, aid, campaignId),
      adGroupTargetCriterion: adGroupId ? readAdGroupTargetCriterion(cid, aid, campaignId, adGroupId) : Promise.resolve(null),
      campaignLocationTargets: readLocationCriterions(cid, aid, campaignId),
      adGroupLocationTargets: adGroupId ? readAdGroupLocationCriterions(cid, aid, campaignId, adGroupId) : Promise.resolve(null),
    };

    return Promise.props(loadPromises)
      .then(async ({
        campaign: rawCampaign,
        adGroup: rawAdGroup,
        campaignScheduleCriterion,
        adGroupScheduleCriterion,
        campaignTargetCriterion,
        adGroupTargetCriterion,
        adGroupLocationTargets,
        campaignLocationTargets,
      }) => {
        const bundleCountriesAndLocationCriterions = await loadBundleCountries({
          cid, aid, serverContext, language: rawCampaign.Languages,
        });
        const { campaignViewModel, adGroupViewModel } = modelToViewModelForAdGroupSetting({
          adGroupModel: rawAdGroup,
          campaignModel: rawCampaign,
          campaignScheduleCriterionModel: campaignScheduleCriterion ? campaignScheduleCriterion.value : null,
          adGroupScheduleCriterionModel: adGroupScheduleCriterion ? adGroupScheduleCriterion.value : null,
          campaignTargetCriterionModel: campaignTargetCriterion ? campaignTargetCriterion.value : null,
          adGroupTargetCriterionModel: adGroupTargetCriterion ? adGroupTargetCriterion.value : null,
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
          adGroupLocationTargets: adGroupLocationTargets ? adGroupLocationTargets.value : [],
          campaignLocationTargets: campaignLocationTargets ? campaignLocationTargets.value : [],
          i18n,
        });

        return Promise.resolve({
          campaign: campaignViewModel,
          adGroup: adGroupViewModel,
          ...bundleCountriesAndLocationCriterions,
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load bid and target settings failed: ${error}`);
        activity.signal(`onBidTargetLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, adGroupId, aid, campaignId, cid, i18n, serverContext]);

  const save = useCallback(({ data }) => {
    const {
      adGroup,
      campaign,
      bundleCountries,
      bundleLocationCriterions,
    } = data;
    const { isPublishing } = state.context.wizardData;
    const adGroupToSave = _.extend(adGroup, { Status: isPublishing ? 'Active' : 'Draft' });

    return saveAdGroup({
      adGroup: adGroupToSave,
      campaign,
      bundleCountries,
      bundleLocationCriterions,
    }, {
      permissions,
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    })
      .then(res => Promise.resolve({
        campaign,
        adGroup: res.adGroup,
      }))
      .catch((error) => {
        const {
          serverErrors = [],
          systemErrors = [],
        } = error || {};

        // eslint-disable-next-line no-console
        console.error(`Save bid and target settings failed: ${error}`);
        activity.signal(`onBidTargetSaveFailed - ${JSON.stringify({
          data,
          error,
        })}`);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          data: {
            adGroup: error.adGroup,
            campaign,
          },
          error: {
            serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
            systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
          },
        });
      });
  }, [activity, i18n, scenario.name, state.context.wizardData]);

  return {
    load,
    save,
  };
};
